import React, { useEffect, useMemo, useState } from "react";
import { Input, Spin, Tag } from "antd";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import IssueKeyBadge from "./IssueKeyBadge";
import UserAvatar from "../../components/UserAvatar";
import * as issuesApi from "../../../../core/data/redux/services/issuesApi";
import { createIssue } from "../../../../core/data/redux/projects/thunks";
import {
  selectActiveCompany,
  selectCurrentProject,
  selectStatuses,
} from "../../../../core/data/redux/projects/selectors";
import {
  STATUS_CATEGORY_COLORS,
} from "../../constants/projects.constants";
import { all_routes } from "../../../router/all_routes";
import { errMsg } from "../../helpers/errMsg.helper";
import { notify } from "../../../../services/toast.config";

import type { Issue } from "../../../../core/data/redux/projects/types";
import type { AppDispatch } from "../../../../core/data/redux/store";

interface SubtasksListProps {
  parentIssue: Issue;
  initialSubtasks?: Issue[];
}

const SubtasksList: React.FC<SubtasksListProps> = ({
  parentIssue,
  initialSubtasks,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const project = useSelector(selectCurrentProject);
  const statuses = useSelector(selectStatuses);
  const activeCompany = useSelector(selectActiveCompany);

  const [subtasks, setSubtasks] = useState<Issue[]>(initialSubtasks || []);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  const todoStatusId = useMemo(
    () =>
      statuses.find((s) => s.category === "TODO")?.id ?? statuses[0]?.id ?? null,
    [statuses]
  );

  const reload = async () => {
    if (!activeCompany.company_id) return;
    setLoading(true);
    try {
      const data = await issuesApi.listSubtasks(
        activeCompany.company_id,
        activeCompany.company_type,
        parentIssue.id
      );
      setSubtasks(Array.isArray(data?.issues) ? data.issues : []);
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to load subtasks"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialSubtasks && initialSubtasks.length > 0) {
      setSubtasks(initialSubtasks);
    } else {
      reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentIssue.id, activeCompany.company_id]);

  const handleQuickAdd = async () => {
    if (!newTitle.trim() || !project || todoStatusId == null) return;
    setAdding(true);
    try {
      await dispatch(
        createIssue({
          project_id: project.id,
          type: "SUBTASK",
          title: newTitle.trim(),
          status_id: todoStatusId,
          priority: "MEDIUM",
          parent_task_id: parentIssue.id,
          sprint_id: parentIssue.sprint_id ?? null,
          epic_id: parentIssue.epic_id ?? null,
        })
      ).unwrap();
      setNewTitle("");
      setShowQuickAdd(false);
      await reload();
      notify.success("Subtask added");
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to add subtask"));
    } finally {
      setAdding(false);
    }
  };

  const statusMap = useMemo(
    () => new Map(statuses.map((s) => [s.id, s])),
    [statuses]
  );

  const doneCount = subtasks.filter((s) => {
    const st = statusMap.get(s.status_id);
    return st?.category === "DONE";
  }).length;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="text-muted small">
          {subtasks.length === 0
            ? "No subtasks"
            : `${doneCount} of ${subtasks.length} done`}
        </div>
        <button
          type="button"
          className="btn btn-sm btn-info"
          onClick={() => setShowQuickAdd((v) => !v)}
        >
          <i className="ti ti-plus me-1" />
          Add subtask
        </button>
      </div>

      {showQuickAdd && (
        <div className="mb-2 d-flex gap-2">
          <Input
            placeholder="Subtask title"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onPressEnter={handleQuickAdd}
            disabled={adding}
            autoFocus
          />
          <button
            type="button"
            className="btn btn-sm btn-success"
            onClick={handleQuickAdd}
            disabled={adding || !newTitle.trim()}
          >
            {adding ? "Adding…" : "Add"}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() => {
              setShowQuickAdd(false);
              setNewTitle("");
            }}
            disabled={adding}
          >
            Cancel
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-2">
          <Spin size="small" />
        </div>
      ) : subtasks.length === 0 ? (
        <p className="text-muted small mb-0">
          {showQuickAdd ? "" : "No subtasks yet."}
        </p>
      ) : (
        <ul className="list-unstyled mb-0 d-flex flex-column gap-1">
          {subtasks.map((s) => {
            const status = statusMap.get(s.status_id);
            const path =
              project &&
              all_routes.projectIssueDetails
                .replace(":projectKey", project.key)
                .replace(":issueKey", s.issue_key);
            return (
              <li
                key={s.id}
                className="d-flex align-items-center gap-2 border rounded p-2"
              >
                <IssueKeyBadge
                  projectKey={project?.key || ""}
                  issueKey={s.issue_key}
                />
                {path ? (
                  <Link
                    to={path}
                    className="flex-grow-1 text-truncate text-dark text-decoration-none"
                  >
                    {s.title}
                  </Link>
                ) : (
                  <span className="flex-grow-1 text-truncate">{s.title}</span>
                )}
                {status && (
                  <Tag
                    color="default"
                    style={{
                      background:
                        status.color || STATUS_CATEGORY_COLORS[status.category],
                      color: "#fff",
                      border: "none",
                      margin: 0,
                    }}
                  >
                    {status.name}
                  </Tag>
                )}
                {s.assignee_snapshot?.name && (
                  <UserAvatar
                    name={s.assignee_snapshot.name}
                    img={s.assignee_snapshot.img}
                    size={22}
                    fontSize={11}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SubtasksList;
